import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Shimmer } from "@/components/Shimmer";
import { ArrowLeft, FileText, Paperclip } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { fmtCurrency, fmtDateTime, fmtBytes, fmtCost, fmtNum, extractName } from "@/lib/format";
import { toast } from "sonner";

export default function OpportunityDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/opportunities/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, [id]);

  if (err) return <div className="p-8 text-rose-700">Error: {err}</div>;
  if (!data) return <div className="p-8"><Shimmer className="h-64" /></div>;

  return (
    <div className="p-8 max-w-[1500px]" data-testid="opp-detail-page">
      <Link to="/opportunities" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-zinc-900 mb-4">
        <ArrowLeft size={14} /> Back to opportunities
      </Link>
      <header className="pb-6 mb-6 border-b border-border">
        <div className="overline mb-2 flex items-center gap-3">
          <span>Opportunity · {data.opportunity_id}</span>
          {data.salesforce?.authoritative && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 text-[10px] font-mono normal-case tracking-normal">
              live salesforce
            </span>
          )}
          {data.salesforce?.error && (
            <span className="px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-800 ring-1 ring-rose-200 text-[10px] font-mono normal-case tracking-normal" title={data.salesforce.error}>
              sf error
            </span>
          )}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight leading-[1.1]">{data.name}</h1>
        <div className="mt-3 flex flex-wrap gap-6 text-xs font-mono text-muted-foreground">
          <span>account <b className="text-zinc-900">{data.account_name || "—"}</b></span>
          <span>stage <b className="text-zinc-900">{data.stage || "—"}</b></span>
          <span>amount <b className="text-zinc-900">{fmtCurrency(data.amount)}</b></span>
          <span>close <b className="text-zinc-900">{data.close_date || "—"}</b></span>
          <span>probability <b className="text-zinc-900">{data.probability ?? "—"}%</b></span>
          {data.owner_id && <span>owner <b className="text-zinc-900">{data.owner_id}</b></span>}
          {data.llm_cost_usd !== null && data.llm_cost_usd !== undefined && data.llm_cost_usd > 0 && (
            <span>LLM cost <b className="text-zinc-900">{fmtCost(data.llm_cost_usd)}</b>
              {data.amount ? (
                <span className="text-emerald-700 ml-2">
                  ({fmtCost(data.llm_cost_usd / (data.amount / 1000))} per $1K deal)
                </span>
              ) : null}
              <span className="text-muted-foreground ml-2">· {fmtNum(data.llm_input_tokens)} in / {fmtNum(data.llm_output_tokens)} out</span>
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-12 gap-4">
        <section className="col-span-12 lg:col-span-7 card-surface" data-testid="opp-emails-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="overline">Source emails · {data.source_emails.length}</div>
          </div>
          <ul className="divide-y divide-border">
            {data.source_emails.map((e) => (
              <li key={e.id} className="px-4 py-3 hover:bg-zinc-50" data-testid={`opp-email-${e.id}`}>
                <Link to={`/emails/${e.id}`} className="flex items-center gap-3">
                  <StatusBadge status={e.status} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{e.subject}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">
                      {extractName(e.from_addr)} · {fmtDateTime(e.ingested_at)}
                    </div>
                  </div>
                  {e.attachment_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs font-mono text-muted-foreground">
                      <Paperclip size={12} />{e.attachment_count}
                    </span>
                  )}
                  {e.llm_cost_usd != null && e.llm_cost_usd > 0 && (
                    <span className="text-xs font-mono text-muted-foreground whitespace-nowrap">{fmtCost(e.llm_cost_usd)}</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="col-span-12 lg:col-span-5 card-surface" data-testid="opp-files-panel">
          <div className="px-4 py-3 border-b border-border">
            <div className="overline">Files · {data.files.length}</div>
          </div>
          <ul className="divide-y divide-border">
            {data.files.map((f) => (
              <li key={f.id} className="px-4 py-3 flex items-center gap-3" data-testid={`opp-file-${f.id}`}>
                <div className="h-9 w-9 rounded bg-zinc-50 border border-border grid place-items-center">
                  <FileText size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{f.filename}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">
                    {f.mime_type || "—"} · {fmtBytes(f.size_bytes)}
                    <span className={`ml-2 px-1 py-0.5 rounded text-[9px] ${f.source === "salesforce" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-700"}`}>
                      {f.source}
                    </span>
                  </div>
                </div>
                {f.source === "s3" && f.email_id ? (
                  <PresignedOpenLink emailId={f.email_id} attachmentId={f.id} />
                ) : (
                  <span className="text-xs font-mono text-muted-foreground">SF-only</span>
                )}
              </li>
            ))}
            {data.files.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted-foreground">No files</li>
            )}
          </ul>
        </section>

        {data.description && (
          <section className="col-span-12 card-surface p-5">
            <div className="overline mb-2">Description</div>
            <p className="text-sm leading-relaxed font-sans whitespace-pre-wrap">{data.description}</p>
          </section>
        )}
      </div>
    </div>
  );
}

function PresignedOpenLink({ emailId, attachmentId }) {
  const [loading, setLoading] = useState(false);
  const open = async () => {
    setLoading(true);
    const tab = window.open("", "_blank", "noopener");
    try {
      const { data } = await api.get(`/emails/${emailId}/attachments/${attachmentId}/url`);
      if (data?.url && tab) tab.location.href = data.url;
      else if (data?.url) window.location.assign(data.url);
      else if (tab) tab.close();
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.detail || "Could not fetch attachment URL");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={open}
      disabled={loading}
      className="text-xs font-mono text-blue-700 hover:underline disabled:opacity-50"
      data-testid={`opp-file-open-${attachmentId}`}
    >
      {loading ? "Opening…" : "Open"}
    </button>
  );
}

